/**
 * safe-html.ts —— v-html XSS 通用净化工具
 *
 * 用途：替换项目中所有 `v-html`、`innerHTML = xxx`，避免后端污染或运营配置导致 XSS。
 *      后端不需要任何改动，全部在前端拦截。
 *
 * 依赖：dompurify ^3
 *   pnpm add dompurify
 *   pnpm add -D @types/dompurify   // Vue 2 + TS 4 项目用得到；Vue 3 + 新版 dompurify 自带类型可省略
 *
 * 适用：Vue 2 / Vue 3 / TSX / SFC 均可。
 *
 * 三档净化等级：
 *   - STRICT  仅保留纯文本（通知、状态字典、Tooltip 等不需要 HTML 的场景）
 *   - BASIC   保留基础排版标签（\n -> <br /> 这种轻量富文本，默认）
 *   - RICH    保留完整安全富文本（运营后台配置的权益说明、公告等）
 *
 * 三档都会强制：
 *   - 移除 <script>/<iframe>/<object>/<embed>/<form>/<style>/<link> 等危险标签
 *   - 移除所有 on* 事件属性（onerror / onload / onclick ...）
 *   - 移除 javascript: / vbscript: / data:（非图片）协议
 *   - <a target="_blank"> 自动补 rel="noopener noreferrer"
 *
 * ---------------------------------------------------------------------------
 * 使用方式 1：函数式（按需调用，最简单）
 *
 *   // .vue
 *   <span v-html="sanitizeHtml(item.notice)"></span>
 *   <div  v-html="sanitizeHtml(right.html, SanitizeLevel.RICH)"></div>
 *
 *   // .tsx
 *   <div v-html={sanitizeHtml(content, SanitizeLevel.RICH)} />
 *
 * 使用方式 2：全局指令 v-safe-html（推荐，一行替换 v-html）
 *
 *   // main.ts
 *   import { vSafeHtml } from '@/utils/safe-html'
 *   app.directive('safe-html', vSafeHtml)
 *
 *   // 模板里
 *   <span v-safe-html="item.notice" />                    // 默认 BASIC
 *   <div  v-safe-html:rich="content" />                   // 指令 arg 指定等级
 *   <div  v-safe-html="[content, 'rich']" />              // 数组形式指定等级
 *   <div  v-safe-html="{ value: content, level: 'rich' }" />
 *
 * 使用方式 3：换行文本转 <br />（替换旧的 text.split('\n').join('<br />')）
 *
 *   <span v-html="newlineToBr(info)"></span>
 *   <span v-html="newlineToBr(tip, ';')"></span>
 * ---------------------------------------------------------------------------
 */

import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify'
import type { Directive, DirectiveBinding } from 'vue'

export enum SanitizeLevel {
  STRICT = 'strict',
  BASIC = 'basic',
  RICH = 'rich',
}

const BASIC_TAGS = ['b', 'strong', 'i', 'em', 'u', 'br', 'p', 'span', 'div']
const RICH_TAGS = [...BASIC_TAGS, 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img']

const SAFE_ATTRS = ['class', 'style', 'id', 'title']
const RICH_ATTRS = [...SAFE_ATTRS, 'href', 'target', 'rel', 'src', 'alt', 'width', 'height']

const CONFIG_BY_LEVEL: Record<SanitizeLevel, DOMPurifyConfig> = {
  [SanitizeLevel.STRICT]: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  },
  [SanitizeLevel.BASIC]: {
    ALLOWED_TAGS: BASIC_TAGS,
    ALLOWED_ATTR: SAFE_ATTRS,
    ALLOW_DATA_ATTR: false,
  },
  [SanitizeLevel.RICH]: {
    ALLOWED_TAGS: RICH_TAGS,
    ALLOWED_ATTR: RICH_ATTRS,
    ALLOW_DATA_ATTR: false,
    // 只放行 http(s)/mailto/tel/相对路径，拦截 javascript:、data:（非图片）等
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  },
}

let purifyHookRegistered = false

const registerPurifyHooks = () => {
  if (purifyHookRegistered) return
  purifyHookRegistered = true
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || ''
      if (/^\s*(javascript|vbscript|data):/i.test(href)) {
        node.removeAttribute('href')
      }
      if (node.hasAttribute('target')) {
        node.setAttribute('rel', 'noopener noreferrer')
      }
    }
    // 双保险：移除任何 on* 事件属性
    for (const attr of Array.from(node.attributes)) {
      if (/^on/i.test(attr.name)) {
        node.removeAttribute(attr.name)
      }
    }
  })
}

/**
 * 净化 HTML 字符串，返回安全的可直接喂给 v-html 的字符串。
 *
 * @param dirty  任意来源的 HTML 片段（字符串、数字、null、undefined 都安全处理）
 * @param level  净化等级，默认 BASIC
 */
export const sanitizeHtml = (dirty: unknown, level: SanitizeLevel = SanitizeLevel.BASIC): string => {
  if (dirty === null || dirty === undefined) return ''
  const input = typeof dirty === 'string' ? dirty : String(dirty)
  if (input === '') return ''
  registerPurifyHooks()
  return DOMPurify.sanitize(input, CONFIG_BY_LEVEL[level]) as unknown as string
}

/**
 * 将含换行/分隔符的纯文本转成安全 HTML，仅把分隔符替换为 <br />。
 * 内部先按 STRICT 等级转义文本，再插入 <br />，用户输入的 <script> 不会被还原。
 */
export const newlineToBr = (input: unknown, splitter: string | RegExp = /\r?\n/): string => {
  if (input === null || input === undefined) return ''
  const text = typeof input === 'string' ? input : String(input)
  const escaped = sanitizeHtml(text, SanitizeLevel.STRICT)
  return escaped.split(splitter as any).join('<br />')
}

/* -------------------------------------------------------------------------- */
/*  Vue 指令 v-safe-html                                                       */
/* -------------------------------------------------------------------------- */

type SafeHtmlBinding = string | number | null | undefined | [unknown, SanitizeLevel | `${SanitizeLevel}`] | { value: unknown; level?: SanitizeLevel | `${SanitizeLevel}` }

const resolveBinding = (binding: DirectiveBinding<SafeHtmlBinding>): { value: unknown; level: SanitizeLevel } => {
  const raw = binding.value
  const argLevel = binding.arg as SanitizeLevel | undefined

  if (Array.isArray(raw)) {
    return {
      value: raw[0],
      level: (raw[1] as SanitizeLevel) || argLevel || SanitizeLevel.BASIC,
    }
  }
  if (raw && typeof raw === 'object' && 'value' in (raw as object)) {
    return {
      value: (raw as { value: unknown }).value,
      level: ((raw as { level?: SanitizeLevel }).level as SanitizeLevel) || argLevel || SanitizeLevel.BASIC,
    }
  }
  return { value: raw, level: argLevel || SanitizeLevel.BASIC }
}

const applySafeHtml = (el: HTMLElement, binding: DirectiveBinding<SafeHtmlBinding>) => {
  const { value, level } = resolveBinding(binding)
  el.innerHTML = sanitizeHtml(value, level)
}

/** Vue 3 指令对象。Vue 2 也可用（见下方 Vue2Adapter） */
export const vSafeHtml: Directive<HTMLElement, SafeHtmlBinding> = {
  mounted: applySafeHtml,
  updated: applySafeHtml,
}

/** Vue 2 兼容写法（Vue 2 项目用这一份） */
export const vSafeHtmlVue2 = {
  bind: applySafeHtml,
  update: applySafeHtml,
}
